import CoralogixOtelWebType from "@coralogix/browser";
import { ObjectUtils, StringUtils } from "@emed-labs/platform-utility-common-utils";
import {
	ConsoleAppender,
	LoggerAppender,
	LoggerContext,
	LoggerEvent,
	SeverityLevel
} from "@emed-labs/platform-utility-logger-context";
import { Patient, Person, VLabRole } from "@emed-labs/virtual-lab-models";
import { get, isString } from "lodash";
import { PropsWithChildren, createContext, useContext, useEffect, useRef } from "react";
import { Config } from "../environment/config";
import { useStateRef } from "../hooks/UseStateRef";
import { EnvironmentContext } from "./EnvironmentProvider";
import * as Sentry from "@sentry/nextjs";
import { CaptureContext, SeverityLevel as SeverityLevelSentry } from "@sentry/core";

export interface UpdateUserProps {
	sessionID: string;
	actor: Person | Patient;
	role: VLabRole;
}

export interface LogContext {
	updateUser: (props: UpdateUserProps) => void;
}

export interface ExtraTags {
	sessionID?: string;
}

export const LogContext = createContext<LogContext>({} as LogContext);
LogContext.displayName = "LogContext";

/**
 * For mapping env file format.
 * .env entry example:
 * LOG_LEVELS="*:debug,aws:warn"
 */
export const EnvLogSeverity = {
	trace: SeverityLevel.TRACE,
	debug: SeverityLevel.DEBUG,
	info: SeverityLevel.INFO,
	warn: SeverityLevel.WARN,
	error: SeverityLevel.ERROR,
	off: SeverityLevel.OFF
};

export const useLogger = (name: string) => {
	const ref = useRef(LoggerContext.create(name));
	return ref.current;
};

const ReplaceStringTokens = (input: string, attributes: Record<string, any>) => {
	return StringUtils.replaceTokens(input, { left: "%", right: "%" }, (s, r) => {
		return get(attributes, r, "");
	});
};

type DeflatedAttributes = Record<string, string | number | boolean>;

export class CoralogixAppender implements LoggerAppender {
	protected pattern: string;
	protected instance: typeof CoralogixOtelWebType.CoralogixRum;
	constructor(instance: typeof CoralogixOtelWebType.CoralogixRum) {
		this.pattern = "[%logger%] %scope% - %message%";
		this.instance = instance;
	}
	removeEmpty = (e) => e;
	levelToString = (e: SeverityLevel) => {
		switch (e) {
			case SeverityLevel.ERROR:
				return "ERROR";
			case SeverityLevel.WARN:
				return "WARN";
			case SeverityLevel.INFO:
				return "INFO";
			case SeverityLevel.DEBUG:
				return "DEBUG";
			case SeverityLevel.TRACE:
				return "TRACE";
		}
	};
	prepare(msg: LoggerEvent, tags: DeflatedAttributes) {
		let tokens = {
			...tags,
			level: this.levelToString(msg.level),
			logger: msg.source.name,
			scope: msg.scope,
			message: isString(msg.message) ? msg.message : JSON.stringify(msg.message),
			context: msg.context.name
		};
		return ReplaceStringTokens(this.pattern, tokens);
	}
	log(event: LoggerEvent) {
		let message = this.prepare(event, event.context.attributes);
		let meta: any = ObjectUtils.removeEmpty({
			attributes: event?.attributes,
			context: event.context
		});
		//allows us to filter by scope in Coralogix in data explorer
		meta.scope = event.scope;
		if (event.attributes?.localConsoleOnly) {
			return;
		}
		switch (event.level) {
			case SeverityLevel.DEBUG:
				this.instance.debug(message, meta);
				break;
			case SeverityLevel.INFO:
				this.instance.info(message, meta);
				break;
			case SeverityLevel.WARN:
				this.instance.warn(message, meta);
				break;
			case SeverityLevel.ERROR:
				this.instance.error(message, meta);
				break;
		}
	}
}

export class SentryAppender implements LoggerAppender {
	log(event: LoggerEvent) {
		const { level, message, attributes, context, source, scope, timestamp } = event;

		let extraTags: ExtraTags = {};
		if (attributes?.sessionID) {
			extraTags.sessionID = attributes.sessionID;
		}

		const captureContext: CaptureContext = {
			extra: {
				...(context?.attributes || {}),
				...(attributes || {}),
				eventTimestamp: timestamp,
				scope,
				loggerContext: context?.name,
				loggerSource: source?.name
			},
			tags: {
				...extraTags,
				logger: source?.name,
				scope,
				context: context?.name
			},
			level:
				level === SeverityLevel.ERROR
					? "error"
					: level === SeverityLevel.WARN
						? "warning"
						: level === SeverityLevel.INFO
							? "info"
							: level === SeverityLevel.DEBUG
								? "debug"
								: "log"
		};

		if (level === SeverityLevel.ERROR) {
			let err: Error | undefined =
				attributes && (attributes as any).error instanceof Error
					? (attributes as any).error
					: message instanceof Error
						? message
						: undefined;

			// If the error is a string (not Error/null), synthesize an Error for stack
			if (!err && attributes?.error && typeof attributes.error === "string") {
				err = new Error(attributes.error);
			}

			// construct error from message if it's still empty
			if (!err) {
				if (typeof message === "string") {
					err = new Error(message);
				} else {
					err = new Error(JSON.stringify(message));
				}
			}

			if (err.name === "Error" && scope) {
				err.name = `${scope}Error`;
			}

			Sentry.captureException(err, captureContext);
		}
	}
}

let sentryInitialized = false; // module-level, only for this bundle

export const LogProvider: React.FunctionComponent<PropsWithChildren> = ({ children }) => {
	const EnvCtx = useContext(EnvironmentContext);
	const [CoralogixRum, setCoralogixRum, CoralogixRumRef] = useStateRef<typeof CoralogixOtelWebType.CoralogixRum>();
	const logger = useLogger("providers.LogProvider");

	useEffect(() => {
		try {
			/**
			 * Add default console appender
			 */
			const appenders: LoggerAppender[] = [new ConsoleAppender("[%logger%] %scope% - %message%", false)];
			if (!EnvCtx.get(Config.FLAG_DISABLE_CORALOGIX_RUM, { optional: true })) {
				const CORALOGIX_RUM_KEY = EnvCtx.get(Config.CORALOGIX_RUM_KEY);
				logger.debug("useEffect", "Initializing CoralogixRum");
				setCoralogixRum(require("@coralogix/browser").CoralogixRum);
				const labels = {
					jurisdiction: EnvCtx.get(Config.JURISDICTION, { optional: true }) || "unknown"
				};
				CoralogixRumRef.current.init({
					labels,
					public_key: CORALOGIX_RUM_KEY,
					application: EnvCtx.get(Config.APP_NAME),
					version: EnvCtx.get(Config.APP_VERSION, { optional: true }) || "1.0",
					coralogixDomain: EnvCtx.get(Config.CORALOGIX_DOMAIN) as any,
					environment: EnvCtx.get(Config.ENVIRONMENT)
				});
				appenders.push(new CoralogixAppender(CoralogixRumRef.current));
			}

			if (
				typeof window !== "undefined" &&
				EnvCtx.get(Config.SENTRY_DSN, { optional: true })
			) {
				// Always add SentryAppender, but only initialize Sentry once
				if (!sentryInitialized) {
					Sentry.init({
						dsn: EnvCtx.get(Config.SENTRY_DSN),
						environment: EnvCtx.get(Config.ENVIRONMENT),
						debug: false,
						release: EnvCtx.get(Config.SENTRY_RELEASE, { optional: true }) || "1.0",
						integrations: (defaults) => defaults.filter((integration) => integration.name !== "CaptureConsole"),
						attachStacktrace: true
					});
					sentryInitialized = true;
				}
				appenders.push(new SentryAppender());
			}

			LoggerContext.useAppender(appenders);
		} catch (error) {
			logger.error("useEffect", "Unable to initialize CoralogixRUM", error);
		}
	}, []);

	useEffect(() => {
		try {
			/**
			 * Configure the logger filters, retrieving from .env file
			 */
			LoggerContext.useFilters(getEnvironmentFilters());
		} catch (error) {
			logger.error("useEffect", "Unable to initialize useFilters", error);
		}
	}, []);

	/**
	 * Translate .env strings into logger filter configuration.
	 *
	 * Example .env:
	 * LOG_LEVELS="*:warn,server:info,services:warn"
	 *
	 * Translated to:
	 * {
	 * 	"*": 3, //SeverityLevel.WARN
	 * 	"server": 2, SeverityLevel.INFO
	 * 	"services": 3 //SeverityLevel.WARN
	 * }
	 * @returns
	 */
	const getEnvironmentFilters = () => {
		const levels = EnvCtx.get(Config.LOG_LEVELS);
		var prepare = levels.split(",").map((item) => {
			const x = item.split(":");
			const severityInt = EnvLogSeverity[x[1]];
			if (isNaN(severityInt)) {
				logger.error("getEnvironmentFilters", `Invalid severity: ${x[1]}`, null, { item });
			} else {
				return {
					[x[0]]: severityInt
				};
			}
		});
		const filters = Object.assign({}, ...prepare);
		logger.debug("getEnvironmentFilters", "filters", { filters });
		return filters;
	};

	const updateUser = (props: UpdateUserProps) => {
		const Identifier = props.actor.identifier[0];
		const user_id = Identifier ? `${Identifier.system}/${Identifier.value}` : `${props.role}`;
		LoggerContext.putAttribute("session_id", props.sessionID);

		if (sentryInitialized) {
			Sentry.setUser({
				id: user_id,
				username: `${props.sessionID}/${props.role}`,
			});
		}
	};
	const value: LogContext = {
		updateUser,
	};
	return <LogContext.Provider value={value}>{children}</LogContext.Provider>;
};
